import React from 'react';
import { useParams } from 'react-router-dom';
import {
  PALMESUS_COLORS,
  PALMESUS_FONT,
  PALMESUS_HEADING_TRACKING,
  getPalmesusCampaign,
} from '../lib/palmesusBrand';

/**
 * A4-utskrivbar plakat. Magenta hovedflate, hvit (negativ) logo,
 * bold Poppins/Gilmer headlines med +10% letter-spacing.
 */
export const PalmesusPoster: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const campaign = getPalmesusCampaign(slug);

  const subtitle = campaign?.subtitle ?? 'Bystranda · 26–27 juni 2026';
  const qrPath = `/palmesus/qr/${slug ?? 'palmesus-demo'}.png`;

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&display=swap"
      />

      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          html, body { margin: 0 !important; padding: 0 !important; background: ${PALMESUS_COLORS.pink} !important; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .no-print { display: none !important; }
          .palmesus-poster { box-shadow: none !important; }
        }
        @media screen { body { background: #1a1a1a; } }
      `}</style>

      <div
        style={{
          minHeight: '100vh',
          padding: '2rem 1rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1.5rem',
        }}
      >
        <button
          className="no-print"
          onClick={() => window.print()}
          style={{
            fontFamily: PALMESUS_FONT,
            fontWeight: 700,
            fontSize: '0.9375rem',
            letterSpacing: PALMESUS_HEADING_TRACKING,
            textTransform: 'uppercase',
            padding: '0.85rem 1.75rem',
            backgroundColor: PALMESUS_COLORS.pink,
            color: PALMESUS_COLORS.white,
            border: 'none',
            cursor: 'pointer',
            transition: 'all 200ms',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = PALMESUS_COLORS.black;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = PALMESUS_COLORS.pink;
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Skriv ut plakat
        </button>

        {/* A4 (210×297mm). 18mm padding gir 174mm content-width. */}
        <div
          className="palmesus-poster"
          style={{
            width: '210mm',
            height: '297mm',
            backgroundColor: PALMESUS_COLORS.pink,
            color: PALMESUS_COLORS.white,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            display: 'flex',
            flexDirection: 'column',
            padding: '20mm 18mm',
            boxSizing: 'border-box',
            fontFamily: PALMESUS_FONT,
          }}
        >
          {/* Topp: logo (negativ, sentrert), godt med luft rundt. */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src="/palmesus/p-logo-white.png"
              alt="Palmesus"
              style={{ width: '52mm', height: 'auto', display: 'block' }}
            />
          </div>

          <div style={{ flex: 1 }} />

          {/* Hero */}
          <div style={{ textAlign: 'center' }}>
            <p
              style={{
                fontFamily: PALMESUS_FONT,
                fontWeight: 700,
                fontSize: '11pt',
                letterSpacing: PALMESUS_HEADING_TRACKING,
                textTransform: 'uppercase',
                color: PALMESUS_COLORS.white,
                opacity: 0.92,
                margin: 0,
              }}
            >
              Skann · Følg · Send inn
            </p>
            <h1
              style={{
                fontFamily: PALMESUS_FONT,
                fontWeight: 800,
                fontSize: '54pt',
                letterSpacing: PALMESUS_HEADING_TRACKING,
                lineHeight: 0.98,
                color: PALMESUS_COLORS.white,
                margin: '6mm 0 0 0',
                textTransform: 'uppercase',
              }}
            >
              Send inn ditt<br />sangønske
            </h1>
            <p
              style={{
                fontFamily: PALMESUS_FONT,
                fontWeight: 500,
                fontSize: '14pt',
                letterSpacing: '0.02em',
                color: PALMESUS_COLORS.white,
                opacity: 0.95,
                margin: '7mm 0 0 0',
                lineHeight: 1.4,
              }}
            >
              Følg @palmesus, kom tilbake, og vi spiller<br />den på Main Stage.
            </p>
          </div>

          <div style={{ flex: 1 }} />

          {/* QR-kort: lys-rosa panel med svart typo, magenta accent. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10mm',
              padding: '10mm',
              backgroundColor: PALMESUS_COLORS.softPink,
              color: PALMESUS_COLORS.black,
            }}
          >
            <img
              src={qrPath}
              alt="QR-kode"
              style={{ width: '54mm', height: '54mm', display: 'block', flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{
                  fontFamily: PALMESUS_FONT,
                  fontWeight: 700,
                  fontSize: '10pt',
                  letterSpacing: PALMESUS_HEADING_TRACKING,
                  textTransform: 'uppercase',
                  color: PALMESUS_COLORS.pink,
                  margin: 0,
                }}
              >
                Skann meg
              </p>
              <p
                style={{
                  fontFamily: PALMESUS_FONT,
                  fontWeight: 800,
                  fontSize: '19pt',
                  letterSpacing: '0.06em',
                  lineHeight: 1.0,
                  color: PALMESUS_COLORS.black,
                  margin: '3mm 0 0 0',
                  textTransform: 'uppercase',
                }}
              >
                Send sangønske
              </p>
              <p
                style={{
                  fontFamily: PALMESUS_FONT,
                  fontWeight: 500,
                  fontSize: '11pt',
                  color: PALMESUS_COLORS.black,
                  opacity: 0.7,
                  margin: '4mm 0 0 0',
                  lineHeight: 1.4,
                }}
              >
                Følg @palmesus først.<br />Vi spiller den på Main Stage.
              </p>
            </div>
          </div>

          {/* Footer */}
          <p
            style={{
              fontFamily: PALMESUS_FONT,
              fontWeight: 700,
              fontSize: '11pt',
              letterSpacing: PALMESUS_HEADING_TRACKING,
              textTransform: 'uppercase',
              color: PALMESUS_COLORS.white,
              opacity: 0.95,
              margin: '8mm 0 0 0',
              textAlign: 'center',
            }}
          >
            {subtitle}
          </p>
        </div>
      </div>
    </>
  );
};

export default PalmesusPoster;
